/**
 * Edit / insert dialog, built entirely from the column registry.
 *
 * Only the fields that exist on the play's unit are shown, grouped Situation /
 * Call / Opponent look / Result / Tags & notes / Custom stats. Catalog stats
 * are selects of the team's ENABLED options (the list Play Entry offers); a
 * retired value already on the play stays selectable, marked "(retired)", so
 * opening and saving a play never silently drops it. Switching the unit
 * rebuilds the form (a defensive snap has no "own formation").
 *
 * Sizing: the dialog is capped to the viewport — header and footer (Save)
 * stay pinned, the body scrolls, and the field grid collapses from four
 * columns to one — so it is fully usable at 1280×720, tablet and phone.
 */
import { useMemo, useState } from 'react';

import { OverlayDialog } from '../../components/overlay-dialog';
import type { PlayView } from '../../lib/api-types';
import { EVENT_REASON_MIN_LENGTH } from '../../lib/constants';
import type { Persona } from '../../lib/personas';
import { PHASE_LABELS, isPlayPhase, type PlayPhase } from '../play-entry/play-catalog';

import { phaseOf, timeText, type ArchiveColumn, type ValueOption } from './archive-columns';
import {
  buildInsertPayload,
  buildPatch,
  draftFromPlay,
  editSectionsFor,
  resolveEditPermission,
  validateDraft,
  type Draft,
  type InsertSide,
  type ResolvedField,
} from './play-edit-model';

export type EditMode =
  | { kind: 'edit'; play: PlayView }
  | { kind: 'insert'; anchor: PlayView; side: InsertSide; position: number; seed: Draft };

export interface PlayEditDialogProps {
  mode: EditMode;
  columns: readonly ArchiveColumn[];
  eventId: string;
  eventStatus: string;
  persona: Persona | null;
  /** Human-readable failure from the last save. */
  error?: string | null;
  /** Field the server rejected. */
  errorField?: string | null;
  onSubmitEdit: (play: PlayView, patch: Record<string, unknown>) => Promise<void>;
  onSubmitInsert: (payload: Record<string, unknown> & { position: number }) => Promise<void>;
  onCancel: () => void;
}

function withCurrent(options: readonly ValueOption[], current: string): ValueOption[] {
  if (
    current === '' ||
    options.some((option) => option.value.toLowerCase() === current.toLowerCase())
  ) {
    return [...options];
  }
  return [...options, { value: current, label: `${current} (retired)` }];
}

function FieldControl({
  entry,
  phase,
  draft,
  error,
  onChange,
}: {
  entry: ResolvedField;
  phase: PlayPhase;
  draft: Draft;
  error: string | null;
  onChange: (name: string, value: string) => void;
}): JSX.Element {
  const { field } = entry;
  const id = `edit-${field.name.replace(/[^a-z0-9_-]/gi, '-')}`;
  const value = draft[field.name] ?? '';
  const label = field.label(phase);
  const describedBy = [
    field.hint === undefined ? null : `${id}-hint`,
    error === null ? null : `${id}-error`,
  ]
    .filter((part) => part !== null)
    .join(' ');
  const common = {
    id,
    'aria-invalid': error === null ? undefined : true,
    'aria-describedby': describedBy === '' ? undefined : describedBy,
  } as const;

  let control: JSX.Element;
  if (field.kind === 'multi') {
    const selected = value
      .split(',')
      .map((entryValue) => entryValue.trim())
      .filter((entryValue) => entryValue !== '');
    const options = withCurrent(field.options?.(phase, draft) ?? [], '');
    control = (
      <div className="edit-chips" role="group" aria-labelledby={`${id}-label`}>
        {options.map((option) => {
          const isOn = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              className="edit-chip"
              aria-pressed={isOn}
              onClick={() =>
                onChange(
                  field.name,
                  (isOn
                    ? selected.filter((entryValue) => entryValue !== option.value)
                    : [...selected, option.value]
                  ).join(', ')
                )
              }
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  } else if (field.kind === 'select') {
    const options = withCurrent(field.options?.(phase, draft) ?? [], value);
    control = (
      <select
        {...common}
        className="edit-field__control"
        value={value}
        onChange={(event) => onChange(field.name, event.target.value)}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  } else if (field.kind === 'textarea') {
    control = (
      <textarea
        {...common}
        className="edit-field__control"
        rows={2}
        value={value}
        onChange={(event) => onChange(field.name, event.target.value)}
      />
    );
  } else {
    control = (
      <input
        {...common}
        className="edit-field__control"
        type="text"
        inputMode={field.kind === 'number' ? 'numeric' : 'text'}
        placeholder={field.placeholder}
        value={value}
        onChange={(event) => onChange(field.name, event.target.value)}
      />
    );
  }

  return (
    <div
      className={`edit-field edit-field--span-${field.span ?? 1}${error === null ? '' : ' edit-field--invalid'}`}
      data-testid={`edit-field-${field.name}`}
    >
      {field.kind === 'multi' ? (
        <span className="edit-field__label" id={`${id}-label`}>
          {label}
        </span>
      ) : (
        <label className="edit-field__label" htmlFor={id}>
          {label}
        </label>
      )}
      {control}
      {field.hint === undefined ? null : (
        <span className="edit-field__hint" id={`${id}-hint`}>
          {field.hint}
        </span>
      )}
      {error === null ? null : (
        <span className="edit-field__error" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function PlayEditDialog({
  mode,
  columns,
  eventId,
  eventStatus,
  persona,
  error = null,
  errorField = null,
  onSubmitEdit,
  onSubmitInsert,
  onCancel,
}: PlayEditDialogProps): JSX.Element {
  const permission = useMemo(
    () => resolveEditPermission(persona, eventStatus),
    [persona, eventStatus]
  );
  const [draft, setDraft] = useState<Draft>(() =>
    mode.kind === 'edit' ? draftFromPlay(mode.play, columns) : { ...mode.seed }
  );
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const phase: PlayPhase = isPlayPhase(draft.possession)
    ? draft.possession
    : ((mode.kind === 'edit' ? phaseOf(mode.play) : null) ?? 'offense');
  const sections = useMemo(() => editSectionsFor(columns, phase, draft), [columns, phase, draft]);

  const trimmedReason = reason.trim();
  const isReasonValid =
    !permission.isReasonRequired || trimmedReason.length >= EVENT_REASON_MIN_LENGTH;

  const title =
    mode.kind === 'edit'
      ? `Edit play #${mode.play.play_number}`
      : `Insert play ${mode.side} #${mode.anchor.play_number}`;

  if (!permission.canEdit) {
    return (
      <OverlayDialog
        title={mode.kind === 'edit' ? `Play #${mode.play.play_number}` : title}
        onClose={onCancel}
        footer={
          <button type="button" className="btn btn--secondary btn--sm" onClick={onCancel}>
            Close
          </button>
        }
      >
        <div className="edit-dialog__blocked">
          <div className="banner banner--warning" role="alert">
            <p className="banner__title">This play cannot be changed</p>
            <p>{permission.deniedReason ?? 'You do not have permission to change plays.'}</p>
          </div>
        </div>
      </OverlayDialog>
    );
  }

  async function handleSubmit(): Promise<void> {
    setFormError(null);
    const nextErrors = validateDraft(draft);
    setErrors(nextErrors);
    const invalid = Object.keys(nextErrors);
    if (invalid.length > 0) {
      const labels = sections
        .flatMap((section) => section.fields)
        .filter((entry) => invalid.includes(entry.field.name))
        .map((entry) => entry.field.label(phase));
      setFormError(
        labels.length === 0
          ? 'Some fields need attention before this play can be saved.'
          : `Check ${labels.join(', ')}.`
      );
      // Bring the first problem into view — the body scrolls, the footer does not.
      globalThis.requestAnimationFrame?.(() => {
        const first = document.querySelector<HTMLElement>(
          '.edit-field--invalid select, .edit-field--invalid input, .edit-field--invalid textarea'
        );
        first?.scrollIntoView?.({ block: 'center' });
        first?.focus({ preventScroll: true });
      });
      return;
    }
    setIsBusy(true);
    try {
      if (mode.kind === 'edit') {
        const patch = buildPatch(mode.play, columns, draft);
        if (Object.keys(patch).length === 0) {
          setFormError('Nothing has changed yet. Edit a field before saving.');
          return;
        }
        if (permission.isReasonRequired) patch.reason = trimmedReason;
        await onSubmitEdit(mode.play, patch);
      } else {
        const payload = buildInsertPayload(eventId, columns, draft, mode.position);
        if (permission.isReasonRequired) payload.reason = trimmedReason;
        await onSubmitInsert(payload);
      }
    } finally {
      setIsBusy(false);
    }
  }

  const bannerText = formError ?? (error !== null && errorField === null ? error : null);
  const formId = 'play-edit-form';

  return (
    <OverlayDialog
      title={title}
      onClose={isBusy ? () => undefined : onCancel}
      panelClassName="overlay__panel--edit"
      headerExtra={
        <span className="edit-dialog__meta">
          {PHASE_LABELS[phase]}
          {mode.kind === 'edit' ? ` · ${timeText(mode.play.stats)}` : ''}
        </span>
      }
      footer={
        <>
          {bannerText === null ? null : (
            <p className="edit-dialog__footer-error" role="alert">
              {bannerText}
            </p>
          )}
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={onCancel}
            disabled={isBusy}
          >
            Cancel
          </button>
          <button
            type="submit"
            form={formId}
            className="btn btn--primary btn--sm"
            disabled={isBusy || !isReasonValid}
          >
            {isBusy
              ? 'Saving…'
              : mode.kind === 'edit'
                ? 'Save correction'
                : `Insert as #${mode.position}`}
          </button>
        </>
      }
    >
      <form
        id={formId}
        className="edit-dialog"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <p className="edit-dialog__lead">
          {mode.kind === 'edit'
            ? 'Your correction is filed as a new version; the previous submission stays on record.'
            : `The new play becomes #${mode.position}. Play #${mode.position} and every later play move down one number.`}
        </p>
        {sections.map((section) => (
          <fieldset key={section.id} className="edit-section" data-section={section.id}>
            <legend className="edit-section__title">{section.label}</legend>
            <div className="edit-section__grid">
              {section.fields.map((entry) => (
                <FieldControl
                  key={entry.field.name}
                  entry={entry}
                  phase={phase}
                  draft={draft}
                  error={
                    errors[entry.field.name] ??
                    (errorField === entry.field.name || errorField === entry.field.customKey
                      ? error
                      : null) ??
                    null
                  }
                  onChange={(name, value) => setDraft((current) => ({ ...current, [name]: value }))}
                />
              ))}
            </div>
          </fieldset>
        ))}
        {permission.isReasonRequired ? (
          <div
            className={`edit-field edit-field--span-3${!isReasonValid && trimmedReason !== '' ? ' edit-field--invalid' : ''}`}
          >
            <label className="edit-field__label" htmlFor="edit-reason">
              Reason for {mode.kind === 'edit' ? 'correction' : 'adding this play'} (required)
            </label>
            <textarea
              id="edit-reason"
              className="edit-field__control"
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <span className="edit-field__hint">
              This event is closed — changes are audited and need at least {EVENT_REASON_MIN_LENGTH}{' '}
              characters.
            </span>
          </div>
        ) : null}
      </form>
    </OverlayDialog>
  );
}
